import React, { useEffect, useState } from "react";
import { FaRegArrowAltCircleLeft } from "react-icons/fa";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addData,
  getData,
  ImageLink,
  updateData,
} from "../services/axios.function";
import UpdateButton from "../components/UpdateButton";

const Card = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state;
  const isEdit = !!editData;

  const [festivalList, setFestivalList] = useState([]);

  const [formData, setFormData] = useState({
    sortOrder: "",
    title: "",
    type: "",
    image: null,
    isActive: true,
  });

  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);

  /* ===============================
   Fetch Festival Dropdown Data
=============================== */
  const fetchFestivals = async () => {
    try {
      const res = await getData("api/festival/get-all");
      if (res?.data?.isSuccess) {
        setFestivalList(res.data.data || []);
      }
    } catch (err) {
      toast.error("Failed to load festival list");
    }
  };

  useEffect(() => {

    // Fetch festival list
    fetchFestivals();

    const fetchLastSortOrder = async () => {
      try {
        const res = await getData("api/card/get-last-sr-no");
        if (res?.data?.isSuccess) {
          const lastNo = res.data.data?.sortOrder || 0;
          setFormData((prev) => ({ ...prev, sortOrder: lastNo + 1 }));
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong!"
        );
      }
    };
    if (!isEdit) fetchLastSortOrder();
    else {
      setFormData({
        sortOrder: editData.sortOrder || "",
        title: editData.title || "",
        image: editData.image || null,
        type: editData.type?._id || "",
        isActive: editData.isActive,
      });
    }
  }, [isEdit, editData]);

  const validateForm = () => {
    const newError = {};
    if (!formData.image && !isEdit)
      newError.image = "Image is required";
    if (!formData.title.trim() && !isEdit) newError.title = "Title is required";
    if (!formData.type)
      newError.type = "Type is required";
    setError(newError);
    return Object.keys(newError).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setFormData((prev) => ({ ...prev, image: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("sortOrder", Number(formData.sortOrder));
      payload.append("title", formData.title);
      payload.append("type", formData.type);
      payload.append("isActive", formData.isActive);
      if (formData.image instanceof File) {
        payload.append("image", formData.image);
      }

      let res;
      if (isEdit) {
        payload.append("_id", editData._id);
        res = await updateData(
          `api/card/update`,
          payload
        );
      } else {
        res = await addData(`api/card/add`, payload);
      }

      if (res?.data?.isSuccess) {
        toast.success(res?.data?.message || "Success!");
        navigate(`/card`);
      } else {
        toast.error(res?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <h4>{isEdit ? "Edit Card" : "Add Card"}</h4>
      </div>
      <form className="add-edit-form" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-lg-6 mb-3">
            <label>Sort Order No:</label>
            <input
              type="text"
              name="sortOrder"
              value={formData.sortOrder}
              // onChange={handleChange}
              className="custom-ip"
              readOnly
            />
          </div>

          <div className="col-lg-6 mb-3">
            <label>Title:</label>
            <input
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              className={`custom-ip ${error.title ? "is-invalid" : ""}`}
            />
            {error.title && (
              <div className="invalid-feedback">{error.title}</div>
            )}
          </div>

          {/* Festival Dropdown */}
          <div className="col-lg-6 mb-3">
            <label>Festival Type:</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`custom-ip ${error.type ? "is-invalid" : ""}`}
            >
              <option value="">Select Festival Type</option>
              {festivalList.map((festival) => (
                <option key={festival._id} value={festival._id}>
                  {festival.name}
                </option>
              ))}
            </select>
            {error.type && (
              <div className="invalid-feedback">{error.festivalId}</div>
            )}
          </div>

          <div className="col-lg-5 mb-3">
            <label>Card Image:</label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className={`custom-ip ${error.image ? "is-invalid" : ""}`}
            />
            {error.image && (
              <div className="invalid-feedback">{error.image}</div>
            )}
            {formData.image && (
              <div className="mt-2">
                <img
                  src={
                    formData.image instanceof File
                      ? URL.createObjectURL(formData.image)
                      : ImageLink(formData.image)
                  }
                  alt="Preview"
                  className="img-thumbnail"
                  style={{ width: "300px" }}
                  onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                />
              </div>
            )}
          </div>
          <div className="col-lg-4 mb-3">
            <table>
              <tbody>
                <tr>
                  <td>isActive</td>
                  <td className="ps-3">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                      />
                      <span className="slider" />
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="d-flex justify-content-center align-items-center gap-4">
          <div className="cancel-btn my-3">
            <Link to={`/card`}>
              <button type="button">
                <FaRegArrowAltCircleLeft size={22} />
                Cancel
              </button>
            </Link>
          </div>
          <div className="submit-btn my-3">
            <UpdateButton loading={loading} isEdit={isEdit} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Card;
